import React from 'react';
import {
  Box,
  Heading,
  Text,
  Flex,
  HStack,
  VStack,
  Badge,
  Button,
  Divider,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useQuery } from 'react-query';
import { usageApi, billingApi, referralsApi } from '../services/api';
import SubscribeModal from '../components/SubscribeModal';

const statusLabel: Record<string, { txt: string; color: string }> = {
  trialing: { txt: 'Em teste', color: 'blue' },
  active: { txt: 'Ativo', color: 'green' },
  pending: { txt: 'Pagamento pendente', color: 'orange' },
  overdue: { txt: 'Inadimplente', color: 'red' },
  canceled: { txt: 'Cancelada', color: 'gray' },
  none: { txt: 'Sem assinatura', color: 'gray' },
};

const Subscription = () => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data: plan, isLoading, refetch } = useQuery('plan-status', usageApi.plan);
  const { data: billing, refetch: refetchBilling } = useQuery('billing-me', billingApi.me);

  const cancel = async () => {
    if (!window.confirm('Cancelar a assinatura? Você mantém o acesso até o fim do período já pago.')) return;
    try {
      const r = await billingApi.cancel();
      toast({ title: 'Assinatura cancelada', description: r.message, status: 'success', duration: 6000, isClosable: true });
      refetch();
      refetchBilling();
    } catch (e: any) {
      toast({ title: 'Falha ao cancelar', description: e?.response?.data?.detail, status: 'error', duration: 4000, isClosable: true });
    }
  };

  if (isLoading || !plan) {
    return <Flex justify="center" py={16}><Spinner color="brand.500" /></Flex>;
  }

  const isTrial = plan.plan === 'trial';
  const isFree = plan.plan === 'gratis' || isTrial;  // sem assinatura paga ainda
  const bstatus = billing?.billing?.status || (isTrial ? 'trialing' : 'none');
  const sl = statusLabel[bstatus] || statusLabel.none;
  const hasSubscription = !!billing?.billing?.subscription_id && bstatus !== 'canceled';

  return (
    <Box maxW="3xl">
      <Heading size="lg" color="brand.700" mb={1}>Assinatura</Heading>
      <Text color="gray.600" mb={6}>Gerencie seu plano, pagamento e período de teste.</Text>

      <Box borderWidth="1px" borderRadius="lg" bg="white" p={6} shadow="sm">
        <Flex justify="space-between" align="center" mb={4}>
          <HStack>
            <Text fontSize="xl" fontWeight="bold">{plan.label}</Text>
            <Badge colorScheme={sl.color}>{sl.txt}</Badge>
          </HStack>
          <HStack>
            <Button colorScheme="brand" onClick={onOpen}>
              {isFree ? 'Assinar' : 'Mudar plano'}
            </Button>
            {hasSubscription && (
              <Button variant="outline" colorScheme="red" onClick={cancel}>Cancelar</Button>
            )}
          </HStack>
        </Flex>

        {/* Plano grátis: convite ao upgrade */}
        {plan.plan === 'gratis' && (
          <Box bg="brand.50" borderRadius="md" p={4} mb={4}>
            <Text color="brand.700">
              Você está no plano <b>Grátis</b>. Assine para liberar conectores (Google Drive, banco de dados),
              mais usuários, transcrição de áudio e modelos premium.
            </Text>
          </Box>
        )}

        {/* Trial: contagem regressiva */}
        {isTrial && (
          <Box bg={plan.trial.expired ? 'red.50' : 'blue.50'} borderRadius="md" p={4} mb={4}>
            {plan.trial.expired ? (
              <Text color="red.700" fontWeight="medium">
                Seu período de teste terminou. Assine um plano para voltar a usar as funções de IA.
              </Text>
            ) : (
              <Text color="blue.700">
                Período de teste: <b>{plan.trial.days_left} {plan.trial.days_left === 1 ? 'dia restante' : 'dias restantes'}</b>
                {plan.trial.ends_at && ` (até ${new Date(plan.trial.ends_at).toLocaleDateString('pt-BR')})`}.
              </Text>
            )}
          </Box>
        )}

        <Divider my={4} />

        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
          <Stat>
            <StatLabel color="gray.500">Usuários</StatLabel>
            <StatNumber fontSize="lg">{plan.usage.users}/{plan.limits.max_users}</StatNumber>
          </Stat>
          <Stat>
            <StatLabel color="gray.500">Bases</StatLabel>
            <StatNumber fontSize="lg">
              {plan.usage.collections}/{plan.limits.max_collections >= 1000 ? '∞' : plan.limits.max_collections}
            </StatNumber>
          </Stat>
          <Stat>
            <StatLabel color="gray.500">Documentos</StatLabel>
            <StatNumber fontSize="lg">{(plan.usage.documents ?? 0).toLocaleString('pt-BR')}/{(plan.limits.max_documents ?? 0).toLocaleString('pt-BR')}</StatNumber>
          </Stat>
          <Stat>
            <StatLabel color="gray.500">Arquivo máx.</StatLabel>
            <StatNumber fontSize="lg">{plan.limits.max_file_mb} MB</StatNumber>
          </Stat>
        </SimpleGrid>

        {bstatus === 'canceled' && (
          <Text mt={4} fontSize="sm" color="gray.500">
            Assinatura cancelada — você mantém o acesso até o fim do período já pago. Para reativar, clique em "Assinar".
          </Text>
        )}
      </Box>

      <ReferralCard />

      <SubscribeModal isOpen={isOpen} onClose={() => { onClose(); refetch(); refetchBilling(); }} />
    </Box>
  );
};

// Card "Indique e ganhe" — laço de indicação (viralização).
const ReferralCard = () => {
  const toast = useToast();
  const { data: ref } = useQuery('referrals-me', referralsApi.me, { staleTime: 60000 });
  if (!ref) return null;
  const copy = () => {
    navigator.clipboard?.writeText(ref.link);
    toast({ title: 'Link copiado!', status: 'success', duration: 2000 });
  };
  return (
    <Box borderWidth="1px" borderRadius="lg" bg="white" p={6} shadow="sm" mt={6} borderColor="brand.200">
      <Heading size="sm" color="brand.700" mb={1}>Indique e ganhe</Heading>
      <Text fontSize="sm" color="gray.600" mb={4}>
        A cada empresa que se cadastrar pelo seu link, <b>você e ela ganham +{ref.bonus_per_referral} documentos</b> de bônus.
      </Text>
      <HStack>
        <Box flex={1} bg="gray.50" borderWidth="1px" borderRadius="md" px={3} py={2} fontSize="sm" color="gray.700" overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis">
          {ref.link}
        </Box>
        <Button colorScheme="brand" size="sm" onClick={copy}>Copiar link</Button>
      </HStack>
      <HStack mt={4} spacing={8} fontSize="sm">
        <Box><Text color="gray.500">Indicações</Text><Text fontWeight="bold" fontSize="lg">{ref.count}</Text></Box>
        <Box><Text color="gray.500">Documentos de bônus</Text><Text fontWeight="bold" fontSize="lg" color="green.600">+{ref.bonus_documents}</Text></Box>
      </HStack>
    </Box>
  );
};

export default Subscription;
